import React, { useEffect, useState } from 'react';
import { Card, Typography, Flex, Spin, message, Modal, Form, Input, InputNumber, DatePicker, Select } from 'antd';
import { useNavigate } from "react-router-dom";
import * as actions from "store/actions/AuthActions";
import dayjs from 'dayjs';
import {
  DndContext,
  DragOverlay,
  pointerWithin,
  rectIntersection,
  getFirstCollision,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  useDroppable,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

// Custom collision detection that prioritizes columns over cards
const customCollisionDetection = (args) => {
  // First, check for collisions with droppable columns
  const pointerCollisions = pointerWithin(args);
  const intersectionCollisions = rectIntersection(args);

  // Combine collisions, preferring pointer collisions
  const collisions = pointerCollisions.length > 0 ? pointerCollisions : intersectionCollisions;

  // Get the first collision
  const firstCollision = getFirstCollision(collisions);

  if (firstCollision) {
    return [firstCollision];
  }

  return collisions;
};

const { Text } = Typography;

// Deal stages configuration with fixed probability percentages
const DEAL_STAGES = [
  { key: 'QUALIFICATION', label: 'Qualification', color: '#e8f5e9', probability: 10 },
  { key: 'NEEDS_ANALYSIS', label: 'Needs Analysis', color: '#e3f2fd', probability: 20 },
  { key: 'VALUE_PROPOSITION', label: 'Value Proposition', color: '#f3e5f5', probability: 40 },
  { key: 'IDENTIFY_DECISION_MAKERS', label: 'Identify Decision Makers', color: '#fff3e0', probability: 60 },
  { key: 'PROPOSAL_PRICE_QUOTE', label: 'Proposal/Price Quote', color: '#e0f2f1', probability: 75 },
  { key: 'NEGOTIATION_REVIEW', label: 'Negotiation/Review', color: '#e1f5fe', probability: 90 },
  { key: 'CLOSED_WON', label: 'Closed Won', color: '#c8e6c9', probability: 100 },
  { key: 'CLOSED_LOST', label: 'Closed Lost', color: '#ffcdd2', probability: 0 },
  { key: 'CLOSED_LOST_TO_COMPETITION', label: 'Closed Lost to Competition', color: '#ffcdd2', probability: 0 },
];

// Sortable Deal Card
const DealCard = ({ deal }) => {
  const navigate = useNavigate();
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: deal.id });

  const style = {
    transform: transform ? CSS.Transform.toString(transform) : undefined,
    transition: transition || undefined,
    opacity: isDragging ? 0.5 : 1,
    cursor: 'move',
  };

  const isOverdue = deal.closing_date && dayjs(deal.closing_date).isBefore(dayjs(), 'day');

  return (
    <div ref={setNodeRef} style={style} {...attributes} {...listeners}>
      <Card
        size="small"
        style={{
          marginBottom: 8,
          backgroundColor: 'white',
          borderLeft: '3px solid #1890ff',
        }}
        hoverable
        onClick={(e) => {
          if (e.target.tagName !== 'BUTTON') {
            navigate(`/deals/edit/${deal.id}`);
          }
        }}
      >
        {deal.deal_number && (
          <div style={{ marginBottom: 4 }}>
            <Text type="secondary" style={{ fontSize: 11 }}>
              {deal.deal_number}
            </Text>
          </div>
        )}
        <div style={{ marginBottom: 4 }}>
          <Text strong>{deal.deal_name}</Text>
        </div>
        <div style={{ marginBottom: 2 }}>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {deal.stage_display}
          </Text>
        </div>
        <div style={{ marginBottom: 2 }}>
          <Text style={{ fontSize: 12 }}>{deal.account_name}</Text>
        </div>
        <div style={{ marginBottom: 2 }}>
          <Text style={{ fontSize: 12 }}>{deal.owner_name}</Text>
        </div>
        <div style={{ marginBottom: 2 }}>
          <Text strong style={{ fontSize: 12 }}>
            Rs. {deal.amount?.toLocaleString() || 0}
          </Text>
        </div>
        {deal.closing_date && (
          <div>
            <Text
              style={{
                fontSize: 11,
                color: isOverdue ? '#f5222d' : '#595959',
              }}
            >
              {dayjs(deal.closing_date).format('DD/MM/YYYY')}
            </Text>
          </div>
        )}
      </Card>
    </div>
  );
};

// Kanban Column
const KanbanColumn = ({ stage, deals, onAddDeal }) => {
  const { setNodeRef, isOver } = useDroppable({
    id: stage,
  });

  const stageConfig = DEAL_STAGES.find(s => s.key === stage) || DEAL_STAGES[0];

  // Calculate total amount by summing up all deal amounts
  const totalAmount = deals.reduce((sum, deal) => {
    const amount = parseFloat(deal.amount) || 0;
    return sum + amount;
  }, 0);

  // Use fixed probability from stage configuration
  const probability = stageConfig.probability;

  return (
    <div
      ref={setNodeRef}
      style={{
        backgroundColor: isOver ? `${stageConfig.color}dd` : stageConfig.color,
        borderRadius: 8,
        padding: 12,
        minWidth: 280,
        maxWidth: 280,
        height: 'calc(100vh - 250px)',
        display: 'flex',
        flexDirection: 'column',
        border: isOver ? '2px dashed #1890ff' : '2px solid transparent',
        transition: 'all 0.2s ease',
      }}
    >
      {/* Column Header */}
      <div style={{ marginBottom: 16, paddingBottom: 12, borderBottom: '2px solid rgba(0,0,0,0.1)' }}>
        <Flex justify="space-between" align="center" style={{ marginBottom: 8 }}>
          <Text strong style={{ fontSize: 14 }}>
            {stageConfig.label}
          </Text>
          <Flex gap={8} align="center">
            <div
              style={{
                backgroundColor: 'white',
                borderRadius: '50%',
                width: 22,
                height: 22,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 11,
                fontWeight: 'bold',
              }}
            >
              {deals.length}
            </div>
            <Text style={{ fontSize: 11, fontWeight: 500 }}>{probability}%</Text>
          </Flex>
        </Flex>
        <Text strong style={{ fontSize: 14, display: 'block' }}>
          Rs. {totalAmount.toLocaleString()}
        </Text>
      </div>

      {/* Droppable Area */}
      <SortableContext items={deals.map(d => d.id)} strategy={verticalListSortingStrategy}>
        <div style={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', minHeight: 100 }}>
          {deals.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 20 }}>
              <Text type="secondary" style={{ fontSize: 12 }}>
                No Deals found.
              </Text>
            </div>
          ) : (
            deals.map((deal) => <DealCard key={deal.id} deal={deal} />)
          )}
        </div>
      </SortableContext>
    </div>
  );
};

// Main Kanban Component
const DealsKanban = () => {
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeId, setActiveId] = useState(null);
  const [originalStage, setOriginalStage] = useState(null);
  const [verifyModalVisible, setVerifyModalVisible] = useState(false);
  const [pendingStageChange, setPendingStageChange] = useState(null);
  const [form] = Form.useForm();

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8, // 8px movement required before drag starts
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  useEffect(() => {
    fetchDeals();
  }, []);

  const fetchDeals = async () => {
    setLoading(true);
    try {
      const response = await actions.loadDeals(1, '', '', '');
      if (response.status === 200) {
        setDeals(response.data.results || []);
      }
    } catch (error) {
      message.error('Failed to load deals');
    }
    setLoading(false);
  };

  const handleDragStart = (event) => {
    const deal = deals.find(d => d.id === event.active.id);
    setActiveId(event.active.id);
    setOriginalStage(deal?.stage);
  };

  const handleDragOver = (event) => {
    const { active, over } = event;

    if (!over) return;

    const activeId = active.id;
    const overId = over.id;

    if (activeId === overId) return;

    const activeDeal = deals.find(d => d.id === activeId);
    if (!activeDeal) return;

    // Check if over is a stage column (stage keys are strings like 'QUALIFICATION')
    const isOverColumn = DEAL_STAGES.some(s => s.key === overId);

    if (isOverColumn) {
      // Dropped on a column
      if (activeDeal.stage !== overId) {
        setDeals(prevDeals => prevDeals.map(deal => {
          if (deal.id === activeId) {
            return { ...deal, stage: overId };
          }
          return deal;
        }));
      }
      return;
    }

    // Over is a deal card
    const overDeal = deals.find(d => d.id === overId);
    if (!overDeal) return;

    if (activeDeal.stage !== overDeal.stage) {
      // Move to different stage (stage of the card we're hovering over)
      setDeals(prevDeals => prevDeals.map(deal => {
        if (deal.id === activeId) {
          return { ...deal, stage: overDeal.stage };
        }
        return deal;
      }));
    }
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;

    const activeId = active.id;

    if (!over) {
      setActiveId(null);
      setOriginalStage(null);
      fetchDeals(); // Revert changes if dropped outside
      return;
    }

    const overId = over.id;

    // Determine the target stage
    let targetStage = null;

    // Check if dropped directly on a column
    const isOverColumn = DEAL_STAGES.some(s => s.key === overId);
    if (isOverColumn) {
      targetStage = overId;
    } else {
      // Dropped on a deal card - get the stage from the deal in current state
      const currentDeal = deals.find(d => d.id === activeId);
      targetStage = currentDeal?.stage;
    }

    // Get the current deal state (after optimistic updates from handleDragOver)
    const currentDeal = deals.find(d => d.id === activeId);
    if (!currentDeal) {
      setActiveId(null);
      setOriginalStage(null);
      return;
    }

    // Use target stage if we dropped on a column, otherwise use the deal's current stage
    const newStage = targetStage || currentDeal.stage;

    // Update the deal with the final stage
    if (newStage !== currentDeal.stage) {
      setDeals(prevDeals => prevDeals.map(deal => {
        if (deal.id === activeId) {
          return { ...deal, stage: newStage };
        }
        return deal;
      }));
    }

    // Check if stage actually changed from original
    const stageChanged = originalStage && newStage !== originalStage;

    setActiveId(null);

    // Update backend if stage changed
    if (stageChanged) {
      // Check if moving to a closed stage - show verification popup
      if (newStage === 'CLOSED_WON' || newStage === 'CLOSED_LOST' || newStage === 'CLOSED_LOST_TO_COMPETITION') {
        setPendingStageChange({
          deal: { ...currentDeal, stage: originalStage }, // Preserve original stage
          newStage
        });
        form.setFieldsValue({
          amount: currentDeal.amount,
          closing_date: currentDeal.closing_date ? dayjs(currentDeal.closing_date) : dayjs(),
          stage: DEAL_STAGES.find(s => s.key === newStage)?.label,
        });
        setVerifyModalVisible(true);
      } else {
        // For other stages (including moving back to earlier stages), update directly
        try {
          // Valid lead source choices from backend
          const validLeadSources = [
            'NONE', 'ADVERTISEMENT', 'COLD_CALL', 'EMPLOYEE_REFERRAL', 'EXTERNAL_REFERRAL',
            'ONLINE_STORE', 'PARTNER', 'PUBLIC_RELATIONS', 'SALES_EMAIL_ALIAS', 'SEMINAR_PARTNER',
            'INTERNAL_SEMINAR', 'TRADE_SHOW', 'WEB_DOWNLOAD', 'WEB_RESEARCH', 'CHAT'
          ];

          // Validate lead_source - only send if it's a valid choice
          const leadSource = currentDeal.lead_source && validLeadSources.includes(currentDeal.lead_source)
            ? currentDeal.lead_source
            : 'NONE';

          // Send the full deal object with updated stage (PUT requires all fields)
          // Set foreign keys to null if they don't exist (orphaned data)
          const updatePayload = {
            deal_name: currentDeal.deal_name,
            amount: currentDeal.amount,
            closing_date: currentDeal.closing_date,
            stage: newStage,
            account: currentDeal.account || null,
            contact: currentDeal.contact || null,
            employee_owner: currentDeal.employee_owner || null,
            probability: currentDeal.probability,
            campaign_source: currentDeal.campaign_source || '',
            lead_source: leadSource,
            deal_type: currentDeal.deal_type || '',
            next_step: currentDeal.next_step || '',
            description: currentDeal.description || '',
            loss_reason: currentDeal.loss_reason || '',
            converted_from_lead: currentDeal.converted_from_lead || null,
          };

          const response = await actions.updateDeal(updatePayload, activeId);

          if (response && (response.status === 200 || response.status === 201)) {
            message.success('Deal stage updated successfully');
            // Don't fetchDeals() here - keep the current order
            // The optimistic update already moved the deal, just confirm it succeeded
          } else {
            message.error('Failed to update deal stage');
            fetchDeals(); // Revert changes only on failure
          }
        } catch (error) {
          // Check for specific validation errors
          const errorData = error.response?.data;
          let errorMsg = 'Failed to update deal stage';

          if (errorData?.account && Array.isArray(errorData.account)) {
            errorMsg = `Deal has invalid account reference. Please edit the deal and fix the account field: ${errorData.account[0]}`;
          } else if (errorData?.contact && Array.isArray(errorData.contact)) {
            errorMsg = `Deal has invalid contact reference. Please edit the deal and fix the contact field: ${errorData.contact[0]}`;
          } else if (errorData?.error) {
            errorMsg = errorData.error;
          } else if (errorData?.message) {
            errorMsg = errorData.message;
          }

          message.error(errorMsg, 5); // Show for 5 seconds
          fetchDeals(); // Revert changes
        }
      }
    } else {
      // No stage change - just reordering within same column
      // For now, we don't persist order to backend
      // If you want to persist order, you'll need a backend field for position/order
    }

    setOriginalStage(null);
  };

  const handleVerifySubmit = async () => {
    try {
      const values = await form.validateFields();

      if (!pendingStageChange) return;

      const { deal, newStage } = pendingStageChange;

      const updatedDeal = {
        ...deal,
        stage: newStage,
        amount: values.amount,
        closing_date: dayjs(values.closing_date).format('YYYY-MM-DD'),
      };

      // Add loss_reason for closed lost stages
      if (newStage === 'CLOSED_LOST' || newStage === 'CLOSED_LOST_TO_COMPETITION') {
        updatedDeal.loss_reason = values.loss_reason;
      }

      await actions.updateDeal(updatedDeal, deal.id);
      message.success('Deal stage updated successfully');

      // Update the deal in the local state with the new values
      setDeals(prevDeals => prevDeals.map(d =>
        d.id === deal.id
          ? { ...d, stage: newStage, amount: values.amount, closing_date: dayjs(values.closing_date).format('YYYY-MM-DD'), loss_reason: updatedDeal.loss_reason }
          : d
      ));

      setVerifyModalVisible(false);
      setPendingStageChange(null);
      form.resetFields();
    } catch (error) {
      if (error.errorFields) {
        // Form validation error
        return;
      }
      message.error('Failed to update deal stage');
      fetchDeals(); // Revert changes
    }
  };

  const handleVerifyCancel = () => {
    setVerifyModalVisible(false);
    setPendingStageChange(null);
    form.resetFields();
    fetchDeals(); // Revert optimistic UI update
  };

  const activeDeal = activeId ? deals.find(d => d.id === activeId) : null;

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: 50 }}>
        <Spin size="large" />
      </div>
    );
  }

  const isClosedLostStage = pendingStageChange?.newStage === 'CLOSED_LOST' || pendingStageChange?.newStage === 'CLOSED_LOST_TO_COMPETITION';

  return (
    <div>
      <DndContext
        sensors={sensors}
        collisionDetection={customCollisionDetection}
        onDragStart={handleDragStart}
        onDragOver={handleDragOver}
        onDragEnd={handleDragEnd}
      >
        <Flex gap={16} style={{ overflowX: 'auto', paddingBottom: 20 }}>
          {DEAL_STAGES.map((stageConfig) => {
            const stageDeals = deals.filter(deal => deal.stage === stageConfig.key);
            return (
              <KanbanColumn
                key={stageConfig.key}
                stage={stageConfig.key}
                deals={stageDeals}
              />
            );
          })}
        </Flex>

        <DragOverlay>
          {activeDeal ? <DealCard deal={activeDeal} /> : null}
        </DragOverlay>
      </DndContext>

      <Modal
        title="Verify Details"
        open={verifyModalVisible}
        onOk={handleVerifySubmit}
        onCancel={handleVerifyCancel}
        okText="Confirm"
        cancelText="Cancel"
        width={500}
      >
        <Form
          form={form}
          layout="vertical"
          initialValues={{
            amount: pendingStageChange?.deal?.amount,
            closing_date: pendingStageChange?.deal?.closing_date ? dayjs(pendingStageChange.deal.closing_date) : dayjs(),
            stage: DEAL_STAGES.find(s => s.key === pendingStageChange?.newStage)?.label,
          }}
        >
          <Form.Item
            label="Amount"
            name="amount"
            rules={[{ required: true, message: 'Please enter the amount' }]}
          >
            <InputNumber
              style={{ width: '100%' }}
              prefix="Rs."
              formatter={value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
              parser={value => value.replace(/Rs\.\s?|(,*)/g, '')}
            />
          </Form.Item>

          <Form.Item
            label="Closing Date"
            name="closing_date"
            rules={[{ required: true, message: 'Please select closing date' }]}
          >
            <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" />
          </Form.Item>

          <Form.Item label="Stage" name="stage">
            <Input disabled />
          </Form.Item>

          {isClosedLostStage && (
            <Form.Item
              label="Reason for Loss"
              name="loss_reason"
              rules={[{ required: true, message: 'Please select a reason for loss' }]}
            >
              <Select placeholder="Select reason">
                <Select.Option value="EXPECTATION_MISMATCH">Expectation Mismatch</Select.Option>
                <Select.Option value="PRICE">Price</Select.Option>
                <Select.Option value="UNQUALIFIED_CUSTOMER">Unqualified Customer</Select.Option>
                <Select.Option value="LACK_OF_RESPONSE">Lack of Response</Select.Option>
                <Select.Option value="MISSED_FOLLOWUPS">Missed Followups</Select.Option>
                <Select.Option value="WRONG_TARGET">Wrong Target</Select.Option>
                <Select.Option value="COMPETITION">Competition</Select.Option>
                <Select.Option value="FUTURE_INTEREST">Future Interest</Select.Option>
                <Select.Option value="OTHER">Other</Select.Option>
                <Select.Option value="NONE">None</Select.Option>
              </Select>
            </Form.Item>
          )}
        </Form>
      </Modal>
    </div>
  );
};

export default DealsKanban;
